(() => {
  const host = document.querySelector("[data-dtfs-home-v2]")
  if (!host || host.dataset.ready === "true") return
  const root = host.shadowRoot || host
  host.dataset.ready = "true"
  document.body.classList.add("dtfs-home-v2-body")

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
  const heroMedia = root.querySelector("[data-dtfs-hero-media]")

  if (heroMedia) {
    const video = heroMedia.querySelector(".dtfs-hero-video")
    const playButton = heroMedia.querySelector("[data-dtfs-hero-play]")

    if (video) {
      const poster = heroMedia.querySelector(".dtfs-hero-brand-stage img")
      const videoSource = window.matchMedia("(max-width: 640px)").matches
        ? video.dataset.srcMobile
        : video.dataset.srcDesktop
      let sourceAttached = false
      let playbackScheduled = false
      if (reducedMotion) {
        video.removeAttribute("autoplay")
        video.pause()
      }
      const retryPlayback = () => attemptPlayback()
      const disarmPlaybackRetry = () => {
        document.removeEventListener("pointerdown", retryPlayback)
        document.removeEventListener("touchstart", retryPlayback)
        document.removeEventListener("keydown", retryPlayback)
      }
      const armPlaybackRetry = () => {
        document.addEventListener("pointerdown", retryPlayback, { once: true, passive: true })
        document.addEventListener("touchstart", retryPlayback, { once: true, passive: true })
        document.addEventListener("keydown", retryPlayback, { once: true })
      }
      const markReady = () => {
        if (reducedMotion) return
        heroMedia.classList.add("dtfs-hero-media--ready")
        if (heroMedia.dataset.mediaState !== "video") heroMedia.dataset.mediaState = "ready"
      }
      const revealVideo = () => {
        disarmPlaybackRetry()
        markReady()
        heroMedia.classList.add("dtfs-hero-media--playing")
        heroMedia.classList.remove("dtfs-hero-media--blocked")
        heroMedia.dataset.mediaState = "video"
      }
      const showPlay = () => {
        heroMedia.classList.remove("dtfs-hero-media--playing")
        heroMedia.classList.add("dtfs-hero-media--blocked")
        heroMedia.dataset.mediaState = "ready"
        armPlaybackRetry()
      }
      const keepPoster = () => {
        heroMedia.classList.remove("dtfs-hero-media--playing", "dtfs-hero-media--ready")
        heroMedia.classList.add("dtfs-hero-media--blocked")
        heroMedia.dataset.mediaState = "poster"
      }
      const attemptPlayback = () => {
        if (!sourceAttached && videoSource) {
          sourceAttached = true
          video.src = videoSource
          video.preload = "auto"
          video.load()
        }
        video.muted = true
        video.defaultMuted = true
        video.playsInline = true
        const playback = video.play()
        if (playback) playback.catch(showPlay)
      }
      const schedulePlayback = () => {
        if (playbackScheduled || reducedMotion) return
        playbackScheduled = true
        const beginPlayback = () => {
          window.setTimeout(() => {
            if ("requestIdleCallback" in window) window.requestIdleCallback(attemptPlayback, { timeout: 2500 })
            else attemptPlayback()
          }, 3000)
        }
        if (window.matchMedia("(max-width: 640px)").matches) {
          const onFirstInteraction = () => {
            ["pointerdown", "touchstart", "scroll", "keydown"].forEach(type => window.removeEventListener(type, onFirstInteraction))
            attemptPlayback()
          }
          ["pointerdown", "touchstart", "scroll", "keydown"].forEach(type => window.addEventListener(type, onFirstInteraction, { once: true, passive: true }))
          return
        }
        if (document.readyState === "complete") beginPlayback()
        else window.addEventListener("load", beginPlayback, { once: true })
      }

      video.addEventListener("loadeddata", markReady, { once: true })
      video.addEventListener("canplay", markReady, { once: true })
      video.addEventListener("playing", revealVideo)
      video.addEventListener("error", keepPoster)
      playButton?.addEventListener("click", attemptPlayback)

      if (reducedMotion) {
        keepPoster()
        heroMedia.classList.remove("dtfs-hero-media--blocked")
      }
      else if (poster?.complete) schedulePlayback()
      else {
        poster?.addEventListener("load", schedulePlayback, { once: true })
        window.setTimeout(schedulePlayback, 1200)
      }
    }
  }

  const lazyVideos = Array.from(root.querySelectorAll("[data-dtfs-lazy-video]"))
  const startLazyVideo = video => {
    if (!video.dataset.src) return
    video.src = video.dataset.src
    delete video.dataset.src
    video.load()
    const playback = video.play()
    if (playback) playback.catch(() => {})
  }

  if (!reducedMotion && navigator.connection?.saveData !== true && lazyVideos.length) {
    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return
          startLazyVideo(entry.target)
          observer.unobserve(entry.target)
        })
      }, { rootMargin: "400px" })
      lazyVideos.forEach(video => observer.observe(video))
    } else {
      lazyVideos.forEach(startLazyVideo)
    }
  }

  root.addEventListener("click", event => {
    const link = event.target.closest('a[href^="#"]')
    if (!link) return
    const target = root.querySelector(link.getAttribute("href"))
    if (!target) return
    event.preventDefault()
    target.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" })
  })

  const spotlight = root.querySelector(".image-spotlight")

  if (spotlight) {
    const target = { x: 0, y: 0 }
    const current = { x: 0, y: 0 }
    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)").matches

    const setInitialPosition = () => {
      const rect = spotlight.getBoundingClientRect()
      target.x = rect.width * 0.58
      target.y = rect.height * 0.46
      current.x = target.x
      current.y = target.y
      spotlight.style.setProperty("--spotlight-x", `${current.x}px`)
      spotlight.style.setProperty("--spotlight-y", `${current.y}px`)
      spotlight.style.setProperty("--spotlight-radius", `${window.innerWidth < 640 ? 280 : 520}px`)
      spotlight.style.setProperty("--spotlight-opacity", finePointer ? "0.12" : "0.72")
    }

    const updatePointer = event => {
      const rect = spotlight.getBoundingClientRect()
      const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom
      if (!inside) {
        if (finePointer) spotlight.style.setProperty("--spotlight-opacity", "0.12")
        return
      }
      target.x = event.clientX - rect.left
      target.y = event.clientY - rect.top
      spotlight.style.setProperty("--spotlight-opacity", "1")
    }

    const easing = 0.14
    let animationFrame = 0
    const animate = () => {
      current.x += (target.x - current.x) * easing
      current.y += (target.y - current.y) * easing
      spotlight.style.setProperty("--spotlight-x", `${current.x}px`)
      spotlight.style.setProperty("--spotlight-y", `${current.y}px`)
      animationFrame = window.requestAnimationFrame(animate)
    }

    setInitialPosition()
    window.addEventListener("resize", setInitialPosition, { passive: true })
    if (finePointer && !reducedMotion) {
      window.addEventListener("pointermove", updatePointer, { passive: true })
      if ("IntersectionObserver" in window) {
        const observer = new IntersectionObserver(entries => {
          const visible = entries.some(entry => entry.isIntersecting)
          if (visible && !animationFrame) animate()
          if (!visible && animationFrame) {
            window.cancelAnimationFrame(animationFrame)
            animationFrame = 0
          }
        }, { rootMargin: "120px" })
        observer.observe(spotlight)
      } else {
        animate()
      }
    }
  }

  const difference = root.querySelector(".dtfs-difference")

  if (difference) {
    const items = Array.from(difference.querySelectorAll(".dtfs-difference__item"))
    const tabs = items.map(item => item.querySelector('[role="tab"]'))
    const panels = Array.from(difference.querySelectorAll(".dtfs-difference__panel"))
    const images = Array.from(difference.querySelectorAll(".dtfs-difference__image"))
    const setDifference = index => {
      items.forEach((item, itemIndex) => {
        const active = itemIndex === index
        item.classList.toggle("is-active", active)
        tabs[itemIndex].setAttribute("aria-selected", String(active))
        tabs[itemIndex].tabIndex = active ? 0 : -1
        panels[itemIndex].hidden = !active
        images[itemIndex].classList.toggle("is-active", active)
        images[itemIndex].setAttribute("aria-hidden", String(!active))
      })
    }

    tabs.forEach((tab, index) => {
      tab.addEventListener("click", () => setDifference(index))
      tab.addEventListener("keydown", event => {
        const keys = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Home", "End"]
        if (!keys.includes(event.key)) return
        event.preventDefault()
        let next = index
        if (event.key === "ArrowUp" || event.key === "ArrowLeft") next = (index - 1 + tabs.length) % tabs.length
        if (event.key === "ArrowDown" || event.key === "ArrowRight") next = (index + 1) % tabs.length
        if (event.key === "Home") next = 0
        if (event.key === "End") next = tabs.length - 1
        setDifference(next)
        tabs[next].focus()
      })
    })
    setDifference(0)
  }

  const faq = root.querySelector("#faq")

  if (faq) {
    const items = Array.from(faq.querySelectorAll("details"))
    const twoColumnFaq = window.matchMedia("(min-width: 1200px)")
    const getFaqGroup = item => twoColumnFaq.matches
      ? Array.from(item.closest(".dtfs-faq__column")?.querySelectorAll("details") || [])
      : items
    const updateFaqState = () => {
      items.forEach(item => {
        const summary = item.querySelector("summary")
        summary?.setAttribute("aria-expanded", String(item.open))
      })
    }

    items.forEach(item => {
      const summary = item.querySelector("summary")
      if (!summary) return

      summary.addEventListener("click", event => {
        event.preventDefault()
        const shouldOpen = !item.open
        getFaqGroup(item).forEach(candidate => {
          candidate.open = shouldOpen && candidate === item
        })
        updateFaqState()
      })

      item.addEventListener("toggle", () => {
        if (item.open) {
          getFaqGroup(item).forEach(candidate => {
            if (candidate !== item) candidate.open = false
          })
        }
        updateFaqState()
      })
    })

    twoColumnFaq.addEventListener("change", () => {
      if (!twoColumnFaq.matches) {
        const openItems = items.filter(item => item.open)
        openItems.slice(1).forEach(item => {
          item.open = false
        })
      }
      updateFaqState()
    })

    updateFaqState()
  }

  if (!reducedMotion && "IntersectionObserver" in window) {
    const production = root.querySelector("#production")
    const productionTargets = production ? Array.from(production.children).slice(1, 4) : []

    if (production && productionTargets.length) {
      productionTargets.forEach(element => {
        element.style.opacity = "0"
        element.style.translate = "0 28px"
      })
      const observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return
          productionTargets.forEach((element, index) => {
          element.animate(
            [{ opacity: 0, translate: "0 28px" }, { opacity: 1, translate: "0 0" }],
            { duration: 900, delay: index * 90, easing: "cubic-bezier(0.22, 1, 0.36, 1)", fill: "both" }
          )
        })
        observer.disconnect()
      }, { threshold: 0.28 })
      observer.observe(production)
    }

    const extensionSections = Array.from(root.querySelectorAll(".dtfs-extension-reveal"))
    extensionSections.forEach(section => {
      const targets = Array.from(section.children[0]?.children || [])
      targets.forEach(element => {
        element.style.opacity = "0"
        element.style.translate = "0 22px"
      })
      const observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return
        targets.forEach((element, index) => {
          element.animate(
            [{ opacity: 0, translate: "0 22px" }, { opacity: 1, translate: "0 0" }],
            { duration: 680, delay: index * 80, easing: "cubic-bezier(0.22, 1, 0.36, 1)", fill: "both" }
          )
        })
        observer.disconnect()
      }, { threshold: 0.16 })
      observer.observe(section)
    })

    const cta = root.querySelector("#cta")
    if (cta) {
      const ctaContent = cta.querySelector(".dtfs-final__grid")
      const ctaTargets = ctaContent ? Array.from(ctaContent.children) : []
      if (ctaTargets.length) {
        ctaTargets.forEach((element, index) => {
          element.style.opacity = "0"
          element.style.translate = `0 ${index === 0 ? 22 : 18}px`
        })
        const observer = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return
          ctaTargets.forEach((element, index) => {
            element.animate(
              [{ opacity: 0, translate: `0 ${index === 0 ? 22 : 18}px` }, { opacity: 1, translate: "0 0" }],
              { duration: index === 0 ? 720 : 660, delay: index * 100, easing: "cubic-bezier(0.22, 1, 0.36, 1)", fill: "both" }
            )
          })
          observer.disconnect()
        }, { threshold: 0.35 })
        observer.observe(cta)
      }
    }
  }

  const focusableSelector = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
  const trapFocus = (event, container) => {
    if (event.key !== "Tab") return
    const focusable = Array.from(container.querySelectorAll(focusableSelector)).filter(element => !element.hidden && element.getClientRects().length)
    if (!focusable.length) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  const mobileMenu = document.getElementById("dtfMobileMenu")
  const mobileOpen = document.getElementById("dtfMobileOpen")
  const mobileClose = document.getElementById("dtfMobileClose")
  if (mobileMenu && mobileOpen && mobileClose) {
    mobileMenu.setAttribute("role", "dialog")
    mobileMenu.setAttribute("aria-modal", "true")
    mobileMenu.setAttribute("aria-label", "Mobile navigation")
    mobileOpen.setAttribute("aria-controls", mobileMenu.id)
    const syncMenu = () => {
      const active = mobileMenu.classList.contains("is-active")
      mobileMenu.inert = !active
      mobileMenu.setAttribute("aria-hidden", String(!active))
      mobileOpen.setAttribute("aria-expanded", String(active))
      if (active) window.requestAnimationFrame(() => mobileClose.focus())
      if (!active && mobileMenu.contains(document.activeElement)) mobileOpen.focus()
    }
    const observer = new MutationObserver(syncMenu)
    observer.observe(mobileMenu, { attributes: true, attributeFilter: ["class"] })
    mobileMenu.addEventListener("keydown", event => {
      if (event.key === "Escape") {
        event.preventDefault()
        mobileClose.click()
        return
      }
      trapFocus(event, mobileMenu)
    })
    mobileMenu.querySelectorAll(".dtf-mobile-nav-toggle").forEach(toggle => {
      const targetId = toggle.dataset.mobileToggle
      if (targetId) toggle.setAttribute("aria-controls", targetId)
      const syncToggle = () => {
        const target = targetId ? document.getElementById(targetId) : null
        toggle.setAttribute("aria-expanded", String(!!target?.classList.contains("is-open")))
      }
      toggle.addEventListener("click", syncToggle)
      syncToggle()
    })
    syncMenu()
  }

  const searchOverlay = document.getElementById("dtfSearchOverlay")
  const searchInput = document.getElementById("dtfSearchInput")
  const searchTriggers = Array.from(document.querySelectorAll("#dtfSearchOpen,.dtf-mobile-search-trigger"))
  if (searchOverlay && searchInput) {
    let searchReturnFocus = null
    searchOverlay.setAttribute("role", "dialog")
    searchOverlay.setAttribute("aria-modal", "true")
    searchOverlay.setAttribute("aria-label", "Search products")
    searchInput.setAttribute("aria-label", "Search products")
    searchTriggers.forEach(trigger => {
      trigger.setAttribute("aria-controls", searchOverlay.id)
      trigger.setAttribute("aria-expanded", "false")
      trigger.addEventListener("click", () => {
        searchReturnFocus = trigger
      })
    })
    const syncSearch = () => {
      const active = searchOverlay.classList.contains("is-active")
      searchOverlay.inert = !active
      searchOverlay.setAttribute("aria-hidden", String(!active))
      searchTriggers.forEach(trigger => trigger.setAttribute("aria-expanded", String(active)))
      if (active) window.setTimeout(() => searchInput.focus(), 120)
      if (!active && searchOverlay.contains(document.activeElement)) {
        const returnTarget = searchReturnFocus?.closest("#dtfMobileMenu[inert]") ? mobileOpen : searchReturnFocus
        returnTarget?.focus()
      }
    }
    const observer = new MutationObserver(syncSearch)
    observer.observe(searchOverlay, { attributes: true, attributeFilter: ["class"] })
    searchOverlay.addEventListener("keydown", event => trapFocus(event, searchOverlay))
    syncSearch()
  }
})()
